import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Download, RefreshCcw } from "lucide-react";
import { PageHeader, Pill, SectionCard } from "../../components/Ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { api, extractError } from "../../lib/api";
import { downloadApiFile } from "../../lib/downloadApiFile";
import { formatListDate } from "../../lib/purchaseListMeta";
import { toast } from "sonner";
import {
  MajorKpiGrid,
  formatCurrency,
  formatPartyKind,
  formatPct,
  formatQty,
} from "../purchase/purchaseReportShared";

const PAGE_SIZE = 50;
const DEFAULT_FILTERS = {
  from_date: "",
  to_date: "",
  variety: "",
  buyer: "",
  broker: "",
  grade: "",
  party_kind: "",
};

export default function SalesReportsPage() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState("item");
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      from_date: filters.from_date || undefined,
      to_date: filters.to_date || undefined,
      variety: filters.variety || undefined,
      buyer: filters.buyer || undefined,
      broker: filters.broker || undefined,
      grade: filters.grade || undefined,
      party_kind: filters.party_kind || undefined,
    }),
    [filters, page],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales-reports/dashboard", { params });
      setPayload(data);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [filters.from_date, filters.to_date, filters.variety, filters.buyer, filters.broker, filters.grade, filters.party_kind]);

  const handleExport = async (mode) => {
    try {
      if (mode === "excel") {
        await downloadApiFile("/sales-reports/export/excel", `sales-${tab}-${Date.now()}.csv`, { ...params, view: tab });
        toast.success("Excel export started");
        return;
      }
      if (mode === "pdf") {
        await downloadApiFile("/sales-reports/export/pdf", `sales-${tab}-${Date.now()}.pdf`, { ...params, view: tab });
        toast.success("PDF export started");
      }
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const voucherMeta = payload?.voucher_log || { count: 0, page: 1, page_size: PAGE_SIZE, results: [] };
  const totalPages = Math.max(1, Math.ceil((voucherMeta.count || 0) / PAGE_SIZE));

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="sales-reports-page">
      <PageHeader
        eyebrow="Sales Reports"
        title="Sales Reporting Dashboard"
        description="Party and broker ledgers, pending vs completed orders, due payments — sales at full contract rate with deductions applied afterwards."
        actions={
          <>
            <Button type="button" variant="outline" onClick={load} disabled={loading}>
              <RefreshCcw className="h-4 w-4 mr-2" />
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("excel")}>Export Excel</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("pdf")}>Export PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <SectionCard title="Global Filters">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            title="From date"
            aria-label="From date"
            value={filters.from_date}
            onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
            className="h-8 w-[9.5rem] shrink-0 px-2 text-sm"
          />
          <Input
            type="date"
            title="To date"
            aria-label="To date"
            value={filters.to_date}
            onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
            className="h-8 w-[9.5rem] shrink-0 px-2 text-sm"
          />
          <Input
            placeholder="Item / variety"
            aria-label="Item / variety"
            value={filters.variety}
            onChange={(e) => setFilters((f) => ({ ...f, variety: e.target.value }))}
            className="h-8 w-36 sm:w-40 shrink-0 px-2 text-sm"
          />
          <Input
            placeholder="Buyer / party"
            aria-label="Buyer / party"
            value={filters.buyer}
            onChange={(e) => setFilters((f) => ({ ...f, buyer: e.target.value }))}
            className="h-8 w-36 sm:w-40 shrink-0 px-2 text-sm"
          />
          <Input
            placeholder="Broker"
            aria-label="Broker"
            value={filters.broker}
            onChange={(e) => setFilters((f) => ({ ...f, broker: e.target.value }))}
            className="h-8 w-32 sm:w-36 shrink-0 px-2 text-sm"
          />
          <Input
            placeholder="Grade"
            aria-label="Grade"
            value={filters.grade}
            onChange={(e) => setFilters((f) => ({ ...f, grade: e.target.value }))}
            className="h-8 w-28 sm:w-32 shrink-0 px-2 text-sm"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { value: "", label: "All" },
              { value: "BUYER", label: "Buyer" },
              { value: "DEALER", label: "Dealer" },
              { value: "BROKER", label: "Broker" },
            ].map((option) => (
              <Button
                key={option.label}
                type="button"
                size="sm"
                variant={filters.party_kind === option.value ? "default" : "outline"}
                className={`h-8 px-2.5 text-xs ${filters.party_kind === option.value ? "bg-bansal-sage hover:bg-bansal-sageDark text-white" : ""}`}
                onClick={() => setFilters((f) => ({ ...f, party_kind: option.value }))}
              >
                {option.label}
              </Button>
            ))}
            <Button type="button" size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={() => setFilters(DEFAULT_FILTERS)}>
              Clear
            </Button>
          </div>
        </div>
      </SectionCard>

      <MajorKpiGrid
        items={[
          { label: "Dispatched qty", value: `${formatQty(payload?.kpis?.dispatched_qtl)} qtl`, accent: true },
          { label: "Pending order qty", value: `${formatQty(payload?.kpis?.pending_order_qtl)} qtl` },
          { label: "Full sales amount", value: formatCurrency(payload?.kpis?.full_sales_amount), sub: "Complete rate · before deductions" },
          { label: "Buyer receivable", value: formatCurrency(payload?.kpis?.buyer_receivable), sub: "After cuts & CD" },
          { label: "Due payments", value: formatCurrency(payload?.kpis?.due_payments), accent: true },
          { label: "Brokerage due", value: formatCurrency(payload?.kpis?.brokerage_due) },
          { label: "Pending orders", value: payload?.kpis?.pending_orders ?? 0 },
          { label: "Completed orders", value: payload?.kpis?.completed_orders ?? 0, sub: `${payload?.kpis?.active_contracts ?? 0} active contracts` },
        ]}
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="item">Item-Wise</TabsTrigger>
          <TabsTrigger value="party">Party Ledger</TabsTrigger>
          <TabsTrigger value="broker">Broker</TabsTrigger>
          <TabsTrigger value="voucher">Voucher Log</TabsTrigger>
          <TabsTrigger value="contract">Contracts</TabsTrigger>
          <TabsTrigger value="pending">Pending Orders</TabsTrigger>
          <TabsTrigger value="completed">Completed Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="item">
          <SectionCard title="Item-Wise Summary" subtitle="Dispatched quantity and full-rate sales by variety.">
            <SimpleTable
              columns={["Variety", "Dispatched", "Vouchers", "Full sales", "Avg full rate", "Buyer receivable", "Due"]}
              rows={(payload?.item_summary || []).map((row) => [
                row.variety,
                `${formatQty(row.dispatched_qtl)} qtl`,
                row.voucher_count,
                formatCurrency(row.full_sales_amount),
                formatCurrency(row.weighted_avg_rate),
                formatCurrency(row.buyer_receivable),
                formatCurrency(row.due_payments),
              ])}
              emptyMessage={loading ? "Loading item summary..." : "No item summary rows match these filters."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="party">
          <SectionCard title="Party-Wise Summary" subtitle="Click a party to open ledger, orders, and due payments.">
            <SimpleTable
              columns={["Party", "Kind", "Contracts", "Contracted", "Dispatched", "Pending qtl", "Full sales", "Due", "CD total"]}
              rows={(payload?.party_summary || []).map((row) => [
                row.party_id ? (
                  <Link key={row.party_id} to={`/sales/reports/party/${row.party_id}`} className="font-semibold link-brand">
                    {row.party_name}
                  </Link>
                ) : (
                  row.party_name
                ),
                <Pill key={`kind-${row.party_id || row.party_name}`} tone="sage">{formatPartyKind(row.party_kind)}</Pill>,
                row.total_contracts ?? 0,
                `${formatQty(row.contracted_qty)} qtl`,
                `${formatQty(row.dispatched_qtl)} qtl`,
                `${formatQty(row.pending_order_qtl)} qtl`,
                formatCurrency(row.full_sales_amount),
                formatCurrency(row.due_payments),
                formatCurrency(row.cd_total),
              ])}
              emptyMessage={loading ? "Loading party summary..." : "No parties match these filters."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="broker">
          <SectionCard title="Broker Summary" subtitle="Brokerage payable from sales at full rate (brokerage tracked separately).">
            <SimpleTable
              columns={["Broker", "Sales qty", "Vouchers", "Full sales", "Brokerage due"]}
              rows={(payload?.broker_summary || []).map((row) => [
                row.broker_party_id ? (
                  <Link key={row.broker_party_id} to={`/sales/reports/party/${row.broker_party_id}`} className="font-semibold link-brand">
                    {row.broker_name}
                  </Link>
                ) : (
                  row.broker_name
                ),
                `${formatQty(row.sales_qtl)} qtl`,
                row.voucher_count,
                formatCurrency(row.full_sales_amount),
                formatCurrency(row.brokerage_due),
              ])}
              emptyMessage={loading ? "Loading broker summary..." : "No broker activity matches these filters."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="voucher">
          <SectionCard title="Sales Voucher Log" subtitle="Full rate → weight/price cuts → CD → buyer receivable; brokerage separate.">
            <SimpleTable
              columns={["Date", "Voucher", "Buyer", "Variety", "Qty", "Full rate", "Full amount", "Cuts+CD", "Receivable", "Due", "Brokerage"]}
              rows={(voucherMeta.results || []).map((row) => [
                formatListDate(row.created_at),
                row.code,
                <div key={`party-${row.id}`} className="min-w-[160px]">
                  <div className="font-medium">{row.party_name}</div>
                  <div className="text-xs text-muted-foreground">{row.broker_name || "No broker"}</div>
                </div>,
                row.variety || "—",
                `${formatQty(row.billable_qtl || row.net_qtl)} qtl`,
                formatCurrency(row.full_rate),
                formatCurrency(row.full_amount),
                formatCurrency(
                  (row.weight_cut_deduction || 0) + (row.price_cut_deduction || 0) + (row.cd_amount || 0),
                ),
                formatCurrency(row.buyer_receivable),
                formatCurrency(row.balance_due),
                formatCurrency(row.brokerage_amount),
              ])}
              emptyMessage={loading ? "Loading voucher log..." : "No sales vouchers match these filters."}
            />
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing page {voucherMeta.page || 1} of {totalPages} · {voucherMeta.count || 0} sales voucher(s)
              </p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <Button type="button" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="contract">
          <SectionCard title="Sales Contract Details" subtitle="Ordered vs dispatched with payment condition from the sales order.">
            <ContractCards rows={payload?.contract_details || []} loading={loading} empty="No contracts match these filters." />
          </SectionCard>
        </TabsContent>

        <TabsContent value="pending">
          <SectionCard title="Pending Sales Orders" subtitle="Contracts still short of ordered quantity.">
            <ContractCards rows={payload?.pending_orders || []} loading={loading} empty="No pending sales orders." />
          </SectionCard>
        </TabsContent>

        <TabsContent value="completed">
          <SectionCard title="Completed Sales Orders" subtitle="Fully dispatched against ordered quantity.">
            <ContractCards rows={payload?.completed_orders || []} loading={loading} empty="No completed sales orders." />
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ContractCards({ rows, loading, empty }) {
  if (!rows.length) {
    return <EmptyText text={loading ? "Loading..." : empty} />;
  }
  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.id} className="rounded-xl border border-border/70 p-4">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-base font-semibold text-foreground">{row.contract_number}</div>
                <Pill tone={row.order_status === "COMPLETED" ? "sage" : "gold"}>{row.order_status || row.status}</Pill>
                {row.status === "ACTIVE" ? <Pill tone="sage">Active</Pill> : null}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {row.buyer_party_id ? (
                  <Link to={`/sales/reports/party/${row.buyer_party_id}`} className="link-brand">
                    {row.buyer_name}
                  </Link>
                ) : (
                  row.buyer_name
                )}
                {" · "}
                {row.variety || "—"}
                {row.grade ? ` · ${row.grade}` : ""}
                {" · "}
                {row.broker_name || "No broker"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Date: {formatListDate(row.contract_date)} · Payment: {row.payment_condition || "—"} · Rate: {formatCurrency(row.agreed_rate)}
                {row.cd_applicable ? ` · CD ${formatPct(row.cd_percent)}` : ""}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 min-w-[320px]">
              <MiniMetric label="Ordered" value={`${formatQty(row.quantity_qtl)} qtl`} />
              <MiniMetric label="Dispatched" value={`${formatQty(row.dispatched_qtl)} qtl`} />
              <MiniMetric label="Pending" value={`${formatQty(row.pending_qtl)} qtl`} />
              <MiniMetric label="Progress" value={formatPct(row.progress_pct)} />
            </div>
          </div>
          <div className="mt-4">
            <div className="h-3 rounded-full bg-muted/80 overflow-hidden">
              <div className="h-full bg-bansal-sage" style={{ width: `${Math.max(0, Math.min(100, row.progress_pct || 0))}%` }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="rounded-lg border border-border/70 px-3 py-2 bg-muted/20">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold text-foreground mt-1">{value}</div>
    </div>
  );
}

function EmptyText({ text }) {
  return <div className="py-12 text-center text-sm text-muted-foreground">{text}</div>;
}

function SimpleTable({ columns, rows, emptyMessage }) {
  if (!rows.length) {
    return <EmptyText text={emptyMessage} />;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <table className="w-full text-sm min-w-[920px]">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground bg-muted border-b border-border">
            {columns.map((column) => (
              <th key={column} className="px-3 py-2">{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-border/60 align-top">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
